import { produce } from "immer";

import { mapRecordingRawToRecording } from "@/features/recordings/helpers";

import {
  createUnavailableContentHubEventSummary,
  mapContentHubEventSummaryRawToContentHubEventSummary,
} from "../event-summaries/helpers";
import type {
  ContentHubEventSummary,
  ContentHubEventSummaryRaw,
} from "../event-summaries/types";
import type { EventActionInput } from "../events/schema";
import type { BrandingPreset, Page } from "../page/domain";
import type {
  BlockCategory,
  Config,
  Recording,
  RecordingRaw,
  SlugValidationError,
  TransactionState,
} from "../types";
import type { LinkedEventSettingsSetupRequest, RootEvent } from "./event";

export interface BlockSelectionPanel {
  isOpen: boolean;
  selectedBlockCategory: BlockCategory;
}

export type MediaUpload = {
  id?: string;
  path?: string;
  url?: string;
  temporaryUrl?: string;
} & TransactionState;

type AuthenticationState =
  | {
      status: "idle" | "authenticating" | "unauthenticated";
      contentApiAuthToken: null;
      contentApiEndpoint: null;
      defaultAuthToken: null;
    }
  | {
      status: "authenticated";
      contentApiAuthToken: string;
      contentApiEndpoint: string;
      defaultAuthToken: string;
    };

export interface ContentHubState {
  organizationExternalId: string | null;
  spaceExternalId: string | null;
  spacesEnabled: boolean;
  page: Page | null;
  pageNotFound: boolean;
  pagePublished: boolean;
  editor: {
    hasNonEditorUnsavedChanges: boolean;
    enabled: boolean;
    currentChangeIndex: number;
    selectedContextMenuTab: number;
  };
  sharingSettings: {
    isOpen: boolean;
    slugValidation: {
      editorActive: boolean;
    } & TransactionState<{ ["pageSlug"]: SlugValidationError }>;
  };
  gatingSettingsOpen: boolean;
  linkedEventSettingsOpen: boolean;
  linkedEventSettingsSetupRequest: LinkedEventSettingsSetupRequest | null;
  readonly: boolean;
  blockSelectionPanel: BlockSelectionPanel | null;
  authentication: AuthenticationState;
  saveTransaction: TransactionState;
  colorPresets: {
    presets: BrandingPreset[];
  } & TransactionState;
  mediaUploads: Record<string, MediaUpload>;
  contentGate: {
    isShowing: boolean;
    contentGateCompleted: boolean;
  };
  analyticsEvent: {
    action?: EventActionInput["action"];
  } & TransactionState;
  recordings: Record<string, Recording>;
  eventSummaries: {
    items: Record<string, ContentHubEventSummary>;
  } & TransactionState;
  config: Config;
}

export const initialState: ContentHubState = {
  organizationExternalId: null,
  spaceExternalId: null,
  spacesEnabled: false,
  page: null,
  pageNotFound: false,
  pagePublished: false,
  authentication: {
    status: "idle",
    contentApiAuthToken: null,
    contentApiEndpoint: null,
    defaultAuthToken: null,
  },
  editor: {
    hasNonEditorUnsavedChanges: false,
    enabled: false,
    currentChangeIndex: 0,
    selectedContextMenuTab: 0,
  },
  sharingSettings: {
    isOpen: false,
    slugValidation: {
      editorActive: false,
      status: "idle",
    },
  },
  gatingSettingsOpen: false,
  linkedEventSettingsOpen: false,
  linkedEventSettingsSetupRequest: null,
  readonly: false,
  blockSelectionPanel: {
    isOpen: false,
    selectedBlockCategory: null,
  },
  saveTransaction: {
    status: "idle",
  },
  colorPresets: {
    status: "idle",
    presets: [],
  },
  mediaUploads: {},
  contentGate: {
    isShowing: false,
    contentGateCompleted: true,
  },
  analyticsEvent: {
    status: "idle",
  },
  recordings: {},
  eventSummaries: {
    status: "idle",
    items: {},
  },
  config: {
    hasContentHubAccess: false,
  },
};

const createEventSummariesMap = (
  eventSummaries: ContentHubEventSummaryRaw[],
  unavailableEventExternalIds: string[] = []
) => ({
  ...eventSummaries.reduce<Record<string, ContentHubEventSummary>>(
    (map, eventSummaryRow) => {
      const eventSummary = mapContentHubEventSummaryRawToContentHubEventSummary(
        eventSummaryRow
      );
      map[eventSummary.id] = eventSummary;
      return map;
    },
    {}
  ),
  ...unavailableEventExternalIds.reduce<Record<string, ContentHubEventSummary>>(
    (map, eventExternalId) => {
      map[eventExternalId] = createUnavailableContentHubEventSummary(
        eventExternalId
      );
      return map;
    },
    {}
  ),
});

const reducer = (state: ContentHubState = initialState, event: RootEvent) =>
  event
    ? produce(state, (draft) => {
        switch (event.type) {
          case "[page editor] initialize editor":
            draft.editor.enabled = true;
            draft.readonly = false;
            draft.authentication.status = "authenticating";
            draft.spaceExternalId = event.payload.spaceExternalId ?? null;
            draft.spacesEnabled = event.payload.spacesEnabled ?? false;
            break;
          case "[page editor] user authentication successful":
            draft.authentication.contentApiEndpoint =
              event.payload.contentApiEndpoint;
            draft.authentication.status = "authenticated";
            draft.authentication.contentApiAuthToken =
              event.payload.contentApiAuthToken;
            draft.organizationExternalId = event.payload.organizationExternalId;
            draft.authentication.defaultAuthToken =
              event.payload.defaultAuthToken;
            break;
          case "[page editor] user authentication failed":
            draft.authentication.status = "unauthenticated";
            break;
          case "[page editor] page load successful":
          case "[page editor] page created":
            draft.page = event.payload.page;
            draft.pagePublished = event.payload.page.published;
            draft.editor.hasNonEditorUnsavedChanges = false;
            break;
          case "[page editor] page title updated":
            draft.page.metadata.title = event.payload.pageTitle;
            draft.editor.hasNonEditorUnsavedChanges = true;
            break;
          case "[page editor] linked event option selected":
            if (
              draft.eventSummaries.items[event.payload.eventExternalId]
                ?.status !== "available"
            ) {
              draft.eventSummaries.items[
                event.payload.eventExternalId
              ] = createUnavailableContentHubEventSummary(
                event.payload.eventExternalId,
                "unavailable",
                event.payload.title
              );
            }
            break;
          case "[page editor] page description updated":
            draft.page.metadata.description = event.payload.pageDescription;
            draft.editor.hasNonEditorUnsavedChanges = true;
            break;

          case "[page editor] sharing image changed":
            draft.page.metadata.image = event.payload.imageMetadata;
            draft.editor.hasNonEditorUnsavedChanges = true;
            break;
          case "[content hub editor] set editor enabled":
            draft.editor.enabled = true;
            break;
          case "[content hub editor] set editor disabled":
            draft.editor.enabled = false;
            break;
          case "[page editor] show share settings":
            draft.sharingSettings.isOpen = true;
            break;
          case "[page editor] hide share settings":
            draft.sharingSettings.isOpen = false;
            draft.sharingSettings.slugValidation = {
              editorActive: false,
              status: "idle",
            };
            break;
          case "[page editor] edit page slug clicked":
            draft.sharingSettings.slugValidation.editorActive = true;
            break;
          case "[page editor] page slug validation started":
            draft.sharingSettings.slugValidation = {
              editorActive: true,
              status: "processing",
            };
            break;
          case "[page editor] page slug validation failed":
            draft.sharingSettings.slugValidation = {
              editorActive: true,
              status: "failed",
              errors: { pageSlug: event.payload.error },
            };
            break;
          case "[page editor] page slug validation successful":
            draft.sharingSettings.slugValidation = {
              editorActive: true,
              status: "success",
            };
            draft.page.slug = event.payload.pageSlug;
            draft.editor.hasNonEditorUnsavedChanges = true;
            break;
          case "[page editor] save page slug clicked":
            draft.sharingSettings.slugValidation.editorActive = false;
            break;
          case "[content hub editor] open block selection panel":
            draft.blockSelectionPanel.isOpen = true;
            draft.blockSelectionPanel.selectedBlockCategory =
              event.payload.blockCategory;
            break;
          case "[content hub editor] close block selection panel":
            draft.blockSelectionPanel.isOpen = false;
            draft.blockSelectionPanel.selectedBlockCategory = null;
            break;
          case "[content hub editor] add block":
            draft.sharingSettings.isOpen = false;
            draft.sharingSettings.slugValidation = {
              editorActive: false,
              status: "idle",
            };
            draft.gatingSettingsOpen = false;
            draft.linkedEventSettingsOpen = false;
            draft.linkedEventSettingsSetupRequest = null;
            draft.editor.selectedContextMenuTab = 0;
            break;
          case "[content hub editor] context menu tab changed":
            draft.editor.selectedContextMenuTab = event.payload.tabIndex;
            break;
          case "[page editor] context menu add block clicked":
            draft.blockSelectionPanel.isOpen = true;
            draft.blockSelectionPanel.selectedBlockCategory =
              event.payload.blockCategory;
            break;
          case "[page editor] undo page history":
            draft.page.content = event.payload.content;
            draft.editor.currentChangeIndex--;
            break;
          case "[page editor] redo page history":
          case "[page editor] page content updated":
            draft.page.content = event.payload.content;
            draft.editor.currentChangeIndex++;
            break;
          case "[page editor] save page started":
            draft.saveTransaction.status = "processing";
            break;
          case "[page editor] save page successful":
            draft.saveTransaction.status = "success";
            draft.page = event.payload.page;
            draft.pagePublished = event.payload.page.published;
            draft.editor.hasNonEditorUnsavedChanges = false;
            draft.editor.currentChangeIndex = 0;
            break;
          case "[page editor] save page failed":
            draft.saveTransaction = {
              status: "failed",
              errors: event.payload.errors,
            };

            if (event.payload.errors["published"]) {
              draft.pagePublished = false;
              draft.page.published = false;
            }
            break;
          case "[page editor] publish page clicked":
            draft.page.published = event.payload.published;
            break;
          case "[page editor] fetch color presets started":
            draft.colorPresets.status = "processing";
            break;
          case "[page editor] fetch color presets successful":
            draft.colorPresets.status = "success";
            draft.colorPresets.presets = event.payload.presets;
            break;
          case "[page editor] fetch color presets failed":
            draft.colorPresets.status = "failed";
            break;
          case "[page editor] colour preset changed":
            draft.page.brandingPreset = event.payload.preset;
            draft.editor.hasNonEditorUnsavedChanges = true;
            break;
          case "[page editor] image upload started":
            draft.mediaUploads[event.payload.uploadTransactionId] = {
              status: "processing",
              temporaryUrl: URL.createObjectURL(event.payload.file),
            };
            break;
          case "[page editor] image uploading":
            draft.mediaUploads[event.payload.uploadTransactionId] = {
              ...draft.mediaUploads[event.payload.uploadTransactionId],
              ...event.payload.mediaItemWithUrl,
              status: "processing",
            };
            break;
          case "[page editor] image upload successful":
            delete draft.mediaUploads[event.payload.uploadTransactionId];
            break;
          case "[page editor] image upload failed":
            draft.mediaUploads[event.payload.uploadTransactionId] = {
              status: "failed",
              errors: {},
            };
            break;
          case "[content page] initialize page":
            draft.organizationExternalId = event.payload.organizationExternalId;
            break;
          case "[content page] initialize preview page":
            draft.organizationExternalId = event.payload.organizationExternalId;
            draft.page = event.payload.page;
            draft.spaceExternalId = event.payload.page.spaceExternalId ?? null;
            draft.recordings = event.payload.recordings.reduce(
              (map, recordingRow: RecordingRaw) => {
                const recording = mapRecordingRawToRecording(recordingRow);
                map[recording.id] = recording;
                return map;
              },
              {}
            );
            draft.eventSummaries = {
              status: "success",
              items: createEventSummariesMap(
                event.payload.eventSummaries,
                event.payload.unavailableEventExternalIds
              ),
            };
            break;
          case "[content page] page load successful":
            draft.page = event.payload.page;
            draft.spaceExternalId = event.payload.page.spaceExternalId ?? null;
            break;
          case "[page editor] show gating settings":
            draft.gatingSettingsOpen = true;
            draft.linkedEventSettingsOpen = false;
            break;
          case "[page editor] hide gating settings":
            draft.gatingSettingsOpen = false;
            break;
          case "[page editor] show linked event settings":
            draft.linkedEventSettingsOpen = true;
            draft.linkedEventSettingsSetupRequest =
              event.payload?.setupRequest ?? null;
            draft.gatingSettingsOpen = false;
            break;
          case "[page editor] hide linked event settings":
            draft.linkedEventSettingsOpen = false;
            draft.linkedEventSettingsSetupRequest = null;
            break;
          case "[page editor] gating type updated":
            draft.page.gatingType = event.payload.gatingType;
            draft.editor.hasNonEditorUnsavedChanges = true;
            break;
          case "[page editor] fetch config successful":
            draft.config = event.payload.config;
            break;
          case "[content page] session validation successful":
            if (
              !event.payload.contentGateCompleted &&
              draft.page?.gatingType === "IMMEDIATE"
            ) {
              draft.contentGate.isShowing = true;
            }
            draft.contentGate.contentGateCompleted =
              event.payload.contentGateCompleted;
            break;
          case "[content page] session validation failed":
            if (draft.page?.gatingType === "IMMEDIATE") {
              draft.contentGate.isShowing = true;
            }
            draft.contentGate.contentGateCompleted = false;
            break;
          case "[content page] show content gate":
            draft.contentGate = {
              ...draft.contentGate,
              isShowing: true,
            };
            break;
          case "[content page] hide content gate":
            draft.contentGate = {
              ...draft.contentGate,
              isShowing: false,
            };
            break;
          case "[content page] submit event":
            draft.analyticsEvent = {
              status: "processing",
              action: event.payload.event.action,
            };
            break;
          case "[content page] submit event successful": {
            const contentGateCompleted = event.payload.contentGateCompleted;
            draft.analyticsEvent = {
              status: "success",
            };
            draft.contentGate = {
              contentGateCompleted,
              isShowing:
                !contentGateCompleted && draft.page.gatingType === "IMMEDIATE",
            };
            break;
          }
          case "[content page] submit event failed":
            draft.analyticsEvent = {
              status: "failed",
              errors: event.payload.errors,
            };
            break;
          case "[page editor] fetch recordings successful": {
            const newRecordingsMap = event.payload.recordings.reduce(
              (map, recordingRow: RecordingRaw) => {
                const recording = mapRecordingRawToRecording(recordingRow);
                map[recording.id] = recording;
                return map;
              },
              {}
            );
            draft.recordings = {
              ...draft.recordings,
              ...newRecordingsMap,
            };
            break;
          }
          case "[page editor] fetch event summaries":
          case "[content page] fetch event summaries":
          case "[content page] fetch all event summaries":
            draft.eventSummaries.status = "processing";
            break;
          case "[page editor] fetch event summaries successful":
          case "[content page] fetch event summaries successful": {
            draft.eventSummaries = {
              status: "success",
              items: {
                ...draft.eventSummaries.items,
                ...createEventSummariesMap(
                  event.payload.eventSummaries,
                  event.payload.unavailableEventExternalIds
                ),
              },
            };
            break;
          }
          case "[page editor] fetch event summaries failed":
          case "[content page] fetch event summaries failed":
            draft.eventSummaries = {
              ...draft.eventSummaries,
              status: "failed",
              errors: event.payload.errors,
            };
            break;
          case "[content page] fetch recordings successful":
            draft.recordings = event.payload.recordings.reduce(
              (map, recordingRow: RecordingRaw) => {
                const recording = mapRecordingRawToRecording(recordingRow);
                map[recording.id] = recording;
                return map;
              },
              {}
            );
            break;
          case "[content page] page not found":
            draft.page = null;
            draft.pageNotFound = true;
            break;
        }
      })
    : state;

export default reducer;
